/**
 * ExportCsvPopover Component
 *
 * Popover for exporting data_ids to CSV format.
 * Developer tier exclusive feature.
 * Shows a dropdown to select which data_id to export and an export button.
 * Uses a portal to render above all other content, positioned relative to anchor.
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { csvExportService } from "../../../services/CsvExportService";
import { buildEnglishLabels } from "../../../features/data-labeling";
import styles from "./ExportCsvPopover.module.css";

interface ExportCsvPopoverProps {
  /** Map of data_id -> description for available data to export */
  dataIds: Record<string, string>;
  /** Callback when popover should close */
  onClose: () => void;
  /** Reference to the anchor element (export icon) */
  anchorRef: React.RefObject<HTMLElement>;
  /** Session ID for fetching data */
  sessionId: string;
  /** Callback when export is successful */
  onExportSuccess?: (filename: string) => void;
}

export const ExportCsvPopover: React.FC<ExportCsvPopoverProps> = ({
  dataIds,
  onClose,
  anchorRef,
  sessionId,
  onExportSuccess,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { user, getToken } = useAuth();
  
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedDataId, setSelectedDataId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dataIdEntries = useMemo(
    () =>
      buildEnglishLabels(
        Object.entries(dataIds).map(([dataId, rawDescription]) => ({
          dataId,
          rawDescription,
        }))
      ),
    [dataIds]
  );

  // Auto-select if only one data_id
  useEffect(() => {
    if (dataIdEntries.length === 1) {
      setSelectedDataId(dataIdEntries[0].dataId);
    }
  }, [dataIdEntries]);

  // Position popover relative to anchor
  useEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current) return;

      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoverWidth = 280;
      const popoverHeight = 180;

      let top = anchorRect.bottom + 8;
      let left = anchorRect.left;

      // Adjust if goes off right edge
      if (left + popoverWidth > window.innerWidth - 16) {
        left = window.innerWidth - popoverWidth - 16;
      }

      // Adjust if goes off bottom edge
      if (top + popoverHeight > window.innerHeight - 16) {
        top = anchorRect.top - popoverHeight - 8;
      }

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorRef]);

  const COMBINED_KEY = "__combined__";

  const handleExport = useCallback(async () => {
    if (!selectedDataId || !user?.email) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        setError("Authentication required");
        return;
      }

      let result;
      if (selectedDataId === COMBINED_KEY) {
        result = await csvExportService.exportCombinedToCsv(
          sessionId,
          dataIds,
          user.email,
          token,
          "quantic_datasheet_combined.csv"
        );
      } else {
        result = await csvExportService.exportToCsv(
          sessionId,
          selectedDataId,
          user.email,
          token,
          "quantic_datasheet.csv"
        );
      }

      if (result.success) {
        onExportSuccess?.(result.filename);
        onClose();
      } else {
        setError(result.error || "Failed to export");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export");
    } finally {
      setIsLoading(false);
    }
  }, [selectedDataId, user?.email, getToken, sessionId, onExportSuccess, onClose]);

  const isValid = Boolean(selectedDataId);

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: position.top, left: position.left }}
    >
      <div className={styles.header}>
        <span className={styles.title}>Export to CSV</span>
      </div>
      <div className={styles.content}>
        {/* Data selection dropdown (only if multiple data_ids) */}
        {dataIdEntries.length > 1 && (
          <div className={styles.paramRow}>
            <label className={styles.label}>Select Data</label>
            <select
              className={styles.select}
              value={selectedDataId}
              onChange={(e) => setSelectedDataId(e.target.value)}
            >
              <option value="">Select data to export...</option>
              <option value={COMBINED_KEY}>✦ Combined (all data)</option>
              {dataIdEntries.map(({ dataId, label }) => (
                <option key={dataId} value={dataId}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Single data display (if only one data_id) */}
        {dataIdEntries.length === 1 && (
          <div className={styles.paramRow}>
            <label className={styles.label}>Data</label>
            <div className={styles.singleDataDisplay}>
              {dataIdEntries[0].label}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className={styles.error}>{error}</div>
        )}

        {/* Action button */}
        <button
          className={styles.exportButton}
          onClick={handleExport}
          disabled={!isValid || isLoading}
        >
          {isLoading ? "Exporting..." : "Export CSV"}
        </button>
      </div>
    </div>,
    document.body
  );
};
